import React from "react";
import { Box, Stack } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { COLORS, RADIUS, deptColorOf } from "./ticketTheme";

function FilterPill({ active, color, label, count, onClick }) {
  const activeColor = color || COLORS.segmentActiveBg;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        border: "1px solid",
        borderColor: active ? activeColor : COLORS.border,
        bgcolor: active ? alpha(activeColor, 0.12) : COLORS.cardBg,
        color: active ? activeColor : COLORS.textPrimary,
        borderRadius: `${RADIUS.pill}px`,
        px: 1.75,
        py: 0.75,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1,
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 120ms ease",
        "&:hover": {
          borderColor: activeColor,
          bgcolor: active ? alpha(activeColor, 0.16) : "#FAFAFC",
        },
      }}
    >
      {color && (
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
          }}
        />
      )}
      <span>{label}</span>
      <Box
        component="span"
        sx={{
          fontWeight: 700,
          opacity: active ? 0.85 : 0.55,
          fontSize: 12,
        }}
      >
        {count || 0}
      </Box>
    </Box>
  );
}

/**
 * Multi-select: `selected` is an array of department ids (not a single
 * value). Clicking a department toggles it in/out of that array — pick
 * several to see the union of tickets across them, click one again to drop
 * it. "All" clears the array entirely (equivalent to no department filter).
 *
 * `counts` always reflects the FULL ticket set (before the department
 * filter is applied) so every chip keeps showing its own true total, the
 * same way the reference design does — narrowing the board to "Dev" alone
 * shouldn't make the "Finance" chip's count disappear.
 */
export default function DepartmentFilterChips({
  departments,
  counts,
  selected,
  onToggle,
  onClear,
}) {
  const selectedSet = new Set((selected || []).map(String));

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        overflowX: "auto",
        minWidth: 0,
        pb: 0.5,
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-thumb": {
          bgcolor: COLORS.border,
          borderRadius: `${RADIUS.pill}px`,
        },
      }}
    >
      <FilterPill
        active={selectedSet.size === 0}
        label="All"
        count={counts.all}
        onClick={onClear}
      />
      {departments.map((dept) => (
        <FilterPill
          key={dept.id}
          active={selectedSet.has(String(dept.id))}
          color={deptColorOf(dept)}
          label={dept.name}
          count={counts[dept.id]}
          onClick={() => onToggle(dept.id)}
        />
      ))}
    </Stack>
  );
}
